import { useQuery } from '@tanstack/react-query';
import React, { ReactNode, useRef } from 'react';
import styled from 'styled-components';

import { Chip, ChipProps, Icon, formatNumber } from '@column/column-ui-kit';
import { ScrollArea, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, cn } from '@column/ui';

import { Avatar } from '~/app/pages/Transfers/_components/Timeline/Avatar';
import { TimelineEventDetails } from '~/app/pages/Transfers/_components/Timeline/TimelineEventDetails';
import { TransferAmount } from '~/app/pages/Transfers/_components/Timeline/types';
import { ROUTE } from '~/app/routes';
import { Details } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { formatTransferType, getTransferIcon } from '~/lib/transfers/utils';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { useSessionStore } from '~/stores/Session';
import { ACHTransferBatchRecord } from '~/typings/API';
import { formatDateTime } from '~/util';

import { TRANSFER_STATUS_META } from './transferHelpers';

interface Props {
  // Null while the sheet animates closed; the last shown transfer is snapshotted so content doesn't blank.
  transfer: ACHTransferBatchRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const dash = (v: string | null | undefined) => (v && v.length > 0 ? v : '-');

// Mirrors the transfer page header's chip icons: muted foreground, drawn inside the Chip.
const mutedIcon = (icon: ReactNode) => (
  <span className="inline-flex text-muted-foreground [&_svg]:[--icon-color:var(--color-muted-foreground)]">{icon}</span>
);

// Effective date that cross-fades local ↔ UTC on hover, mirroring the transfer page header. The two
// renderings are stacked in one grid cell so the block sizes to the wider string and doesn't reflow.
const HoverDate: React.FC<{ value: string }> = ({ value }) => (
  <span className="group inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground [&_svg]:[--icon-color:var(--color-muted-foreground)]">
    <Icon.Calendar />
    <span className="grid">
      <span className="col-start-1 row-start-1 whitespace-nowrap transition-opacity duration-150 group-hover:opacity-0">
        {formatDateTime(value, { timeZoneFormat: 'local' })}
      </span>
      <span
        aria-hidden
        className="col-start-1 row-start-1 whitespace-nowrap opacity-0 transition-opacity duration-150 group-hover:opacity-100"
      >
        {formatDateTime(value, { timeZoneFormat: 'utc' })}
      </span>
    </span>
  </span>
);

// Gray pending dot + connector line, matching the transfers-page timeline's not-yet-initiated markers.
const RailDot = styled.span`
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};

  svg {
    --icon-size: 18px;
    --icon-color: currentColor;
  }
`;

const RailLine = styled.span`
  background: ${({ theme }) => theme.secondary.blendToBackground(200)};
`;

// Zero the shared card's own (viewport-based) margins; the rail supplies the left offset and pb the gap.
const CardSlot = styled.div`
  min-width: 0;
  flex: 1;

  & > * {
    margin: 0 !important;
  }
`;

// The two parties on the shared TimelineEventDetails card, joined by the gray pending rail.
const PartyTimeline: React.FC<{
  cards: { key: string; props: React.ComponentProps<typeof TimelineEventDetails> }[];
}> = ({ cards }) => (
  <div className="flex flex-col">
    {cards.map((card, index) => {
      const isLast = index === cards.length - 1;
      return (
        <div key={card.key} className="flex gap-3">
          <div className="flex w-[18px] shrink-0 flex-col items-center">
            <RailDot className="mt-3.5">
              <Icon.CircleBlank />
            </RailDot>
            {!isLast && <RailLine className="mt-1 -mb-2.5 w-0.5 flex-1 rounded" />}
          </div>
          <CardSlot className={cn(!isLast && 'pb-4')}>
            <TimelineEventDetails {...card.props} />
          </CardSlot>
        </div>
      );
    })}
  </div>
);

export const TransferDetailsSheet: React.FC<Props> = ({ transfer, open, onOpenChange }) => {
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const navigate = useNavigate();

  // Keep rendering the last-shown transfer while the sheet animates out (transfer goes null on close).
  const shownRef = useRef<ACHTransferBatchRecord | null>(null);
  if (transfer) {
    shownRef.current = transfer;
  }
  const record = transfer ?? shownRef.current;

  const { data: accountNumber } = useQuery({
    queryKey: [platformId, 'account-number', record?.accountNumberId],
    queryFn: () => BankAccountRepository.getAccountNumber({ id: record?.accountNumberId ?? '' }),
    enabled: !!record?.accountNumberId,
    staleTime: 30000,
  });

  // Source bank account's display name for the timeline card title.
  const { data: bankAccount } = useQuery({
    queryKey: [platformId, 'bank-account', record?.bankAccountId],
    queryFn: () => BankAccountRepository.get({ id: record?.bankAccountId ?? '' }),
    enabled: !!record?.bankAccountId,
    staleTime: 30000,
  });

  if (!record) {
    return <Sheet open={open} onOpenChange={onOpenChange} />;
  }

  const bankAccountName = bankAccount?.displayName || bankAccount?.description || 'Bank account';

  const isDebit = record.type === 'debit';
  const currency = record.currency || 'USD';
  const transferEntryType = isDebit ? 'ach_debit' : 'ach_credit';
  const statusMeta = TRANSFER_STATUS_META[record.status] ?? {
    type: 'default' as ChipProps['type'],
    label: record.status,
    icon: <Icon.CircleBlank />,
  };

  // On a credit our account originates (outgoing); on a debit it collects (incoming).
  const bankOutgoing = !isDebit;
  // A truthy subtitle just gates the card's amount line; the amount itself renders from `amount`.
  const partyAmount = (outgoing: boolean): TransferAmount => ({
    value: record.amount,
    isAdding: !outgoing,
    currencyCode: currency,
  });

  const bankCard = {
    key: 'bank',
    props: {
      icon: (
        <Avatar size="large">
          <Icon.Business />
        </Avatar>
      ),
      title: bankAccountName,
      onClick: record.bankAccountId ? () => navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${record.bankAccountId}`) : undefined,
      subtitle: ' ',
      amount: partyAmount(bankOutgoing),
      metadata: bankOutgoing ? 'Outgoing' : 'Incoming',
      details: [
        { label: 'Bank Account ID', value: dash(record.bankAccountId) },
        { label: 'Routing Number', value: dash(accountNumber?.routingNumber) },
        { label: 'Account Number', value: dash(accountNumber?.accountNumber ?? record.accountNumberId) },
      ],
    },
  };
  const receiverCard = {
    key: 'receiver',
    props: {
      icon: (
        <Avatar size="large">
          <Icon.User />
        </Avatar>
      ),
      title: record.receiverName || 'Receiver',
      // Only link when the receiver resolved to a saved counterparty; a raw NACHA receiver has none.
      onClick: record.counterpartyId
        ? () => navigate(`${ROUTE.COUNTERPARTIES}/edit/${record.counterpartyId}`)
        : undefined,
      subtitle: ' ',
      amount: partyAmount(!bankOutgoing),
      metadata: bankOutgoing ? 'Incoming' : 'Outgoing',
      details: [
        { label: 'Receiver ID', value: dash(record.receiverId) },
        { label: 'Routing Number', value: dash(record.receiverRoutingNumber) },
        { label: 'Account Number', value: dash(record.receiverAccountNumber) },
      ],
    },
  };
  // Source of funds (outgoing) sits on top so the rail reads as the money's direction of travel.
  const cards = bankOutgoing ? [bankCard, receiverCard] : [receiverCard, bankCard];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        // Avoid auto-focusing the first field (a copy input that would select/highlight its text on open).
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="flex w-full max-w-[600px] flex-col gap-0 border-l-0 p-0 shadow-[-8px_0_24px_rgba(0,0,0,0.12)] sm:max-w-[600px]"
      >
        <SheetHeader className="gap-0 border-b border-border px-5 pb-4 pt-4">
          <SheetTitle className="text-base font-medium">ACH Transfer</SheetTitle>
          <SheetDescription className="sr-only">ACH transfer details</SheetDescription>
        </SheetHeader>

        <ScrollArea fadeEdges className="min-h-0 flex-1 !rounded-none">
          {/* Amount, then a row of markers: status, type, direction, effective date. Even py so the block
              breathes equally top and bottom; a flush divider runs along the sheet's edges. */}
          <div className="border-b border-border px-5 py-5">
            <div
              className={cn(
                'text-2xl font-semibold leading-7 tracking-[-0.02em]',
                isDebit ? '[color:var(--color-seafoam-600)]' : 'text-foreground'
              )}
            >
              {`${isDebit ? '' : '-'}${formatNumber(record.amount, currency)}`}
              <span className="ml-1 text-sm font-medium text-muted-foreground">{currency}</span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              {/* Status marker, colored by type, with the table's check icon (left) for valid/succeeded. */}
              <Chip icon={statusMeta.icon} size="large" variant="subtle" type={statusMeta.type}>
                {statusMeta.label}
              </Chip>
              <Chip icon={mutedIcon(getTransferIcon(transferEntryType))} size="large" variant="subtle">
                {formatTransferType(transferEntryType, { abbreviated: false })}
              </Chip>
              <Chip
                icon={mutedIcon(isDebit ? <Icon.CircleArrowDown /> : <Icon.CircleArrowUp />)}
                size="large"
                variant="subtle"
              >
                {isDebit ? 'Incoming' : 'Outgoing'}
              </Chip>
              {record.effectiveOn && <HoverDate value={record.effectiveOn} />}
            </div>
          </div>

          {/* The two parties on the shared timeline card, in a not-yet-initiated (gray) rail. */}
          <div className="border-b border-border px-5 py-5">
            <PartyTimeline cards={cards} />
          </div>

          {/* Every field of the record, auto-listed as copy rows (Formatted) with a raw JSON toggle. In the
              sheet's narrow container the label stacks above the card (see Details' container query). !p-0
              drops Details' own page-margin padding so it aligns with the sections above. */}
          {/* pt-2 + the Details label's own 12px top margin ≈ the 20px rhythm of the sections above. */}
          <div className="px-5 pb-6 pt-2">
            <Details
              stacked
              className="!p-0"
              label="Transfer details"
              formattedData={record as unknown as Record<string, unknown>}
              rawData={record as unknown as Record<string, unknown>}
            />
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
